import React, { useState } from "react";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import PublicIcon from "@mui/icons-material/Public";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CollectionsIcon from "@mui/icons-material/Collections";
import BlurhashImage from "./BlurhashImage";
import GalleryDetailModal from "./GalleryDetailModal";

export default function GalleryGrid({ items, isOwner, onToggleVisibility, onDelete }) {
  const [selected, setSelected] = useState(null);

  if (!items || items.length === 0) {
    return (
      <Box sx={{ py: 8, textAlign: "center", color: "#666" }}>
        <Typography>No images yet</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "repeat(3, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(4, 1fr)" },
        gap: { xs: "2px", sm: "4px" },
      }}
    >
      {items.map((item) => {
        const isPrivate = item.visibility === "private";
        const hasUrl = !!item.filePath;
        return (
          <Box
            key={item._id}
            onClick={() => hasUrl && setSelected(item)}
            sx={{
              position: "relative",
              aspectRatio: "1 / 1",
              overflow: "hidden",
              background: "#111",
              cursor: hasUrl ? "pointer" : "default",
              "&:hover .gallery-tile-actions": { opacity: 1 },
            }}
          >
            {hasUrl ? (
              <img
                src={item.filePath}
                alt={item.caption || ""}
                loading="lazy"
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
            ) : item.blurhash ? (
              <BlurhashImage hash={item.blurhash} width={32} height={32} />
            ) : (
              <Box sx={{ width: "100%", height: "100%", background: "#222" }} />
            )}

            {isPrivate && (
              <Box
                sx={{
                  position: "absolute",
                  top: 6,
                  left: 6,
                  background: "rgba(0,0,0,0.55)",
                  borderRadius: "50%",
                  width: 24,
                  height: 24,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LockIcon sx={{ fontSize: 14, color: "#FFEE20" }} />
              </Box>
            )}

            {item.imageCount > 1 && (
              <Box
                sx={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.7))",
                }}
                title={`${item.imageCount} images`}
              >
                <CollectionsIcon sx={{ fontSize: 18 }} />
              </Box>
            )}

            {isOwner && (
              <Stack
                direction="row"
                spacing={0.5}
                className="gallery-tile-actions"
                sx={{
                  position: "absolute",
                  bottom: 6,
                  right: 6,
                  opacity: 0,
                  transition: "opacity 0.2s",
                }}
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleVisibility(item);
                  }}
                  title={isPrivate ? "Make public" : "Make private"}
                  sx={{
                    background: "rgba(0,0,0,0.6)",
                    color: "#fff",
                    "&:hover": { background: "rgba(0,0,0,0.8)", color: "#FFEE20" },
                  }}
                >
                  {isPrivate ? <PublicIcon sx={{ fontSize: 16 }} /> : <LockIcon sx={{ fontSize: 16 }} />}
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(item);
                  }}
                  title="Delete"
                  sx={{
                    background: "rgba(0,0,0,0.6)",
                    color: "#fff",
                    "&:hover": { background: "rgba(0,0,0,0.8)", color: "#ff5252" },
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
            )}
          </Box>
        );
      })}

      <GalleryDetailModal
        item={selected}
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
      />
    </Box>
  );
}
